/**
 * @fileoverview API endpoint configuration for the Snapped application.
 * Contains all API endpoint URLs and helper functions for constructing endpoint paths.
 * Centralizes API route management and provides a single source of truth for API URLs.
 */

// Get the API base URL from environment variable or fallback to production
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://track.snapped.cc';
const BUNNY_CDN_URL = 'https://snapped2.b-cdn.net';

/**
 * @namespace API_ENDPOINTS
 * @description Collection of all API endpoints used in the application.
 * Organized by feature/domain for better maintainability.
 */
export const API_ENDPOINTS = {
  /** @property {string} API_BASE_URL - Base URL for all API calls */
  API_BASE_URL: API_BASE_URL,
  
  /** @property {string} CALL_FORM - Endpoint for call form submissions */
  CALL_FORM: `${API_BASE_URL}/call`,
  
  /** @property {string} CALL_FORM_MOXY - Endpoint for Moxy-specific call form submissions */
  CALL_FORM_MOXY: `${API_BASE_URL}/call-moxy`,
  
  /** @property {string} CALL_FORM_WAVE - Endpoint for SnappedWave call form submissions */
  CALL_FORM_WAVE: `${API_BASE_URL}/call`,
  
  /** @property {string} CALL_FORM_COLLAB - Endpoint for Collab call form submissions */
  CALL_FORM_COLLAB: `${API_BASE_URL}/call`,
  
  /** @property {string} LEAD_FORM - Endpoint for lead form submissions */
  LEAD_FORM: `${API_BASE_URL}/api/leads/submit`,
  
  /** @property {string} LEAD_FORM_SNAPPEDWAVE - Endpoint for SnappedWave lead form submissions */
  LEAD_FORM_SNAPPEDWAVE: `${API_BASE_URL}/api/leads/submit-snappedwave`,
  
  /** 
   * @function SOCIAL_STATS
   * @param {string} platform - Social media platform
   * @param {string} username - User's social media handle
   * @returns {string} Endpoint for fetching social media statistics
   */
  SOCIAL_STATS: (platform, username) => 
    `${API_BASE_URL}/api/social-stats/${platform}/${username}`,
  
  /** @property {string} UPLOAD_ACTIVITY - Endpoint for upload activity tracking */
  UPLOAD_ACTIVITY: `${API_BASE_URL}/api/uploadapp/upload-activity`,
  
  /** @property {string} UPLOAD_ACTIVITY_SECURE - Endpoint for secure upload activity tracking (web app only) */
  UPLOAD_ACTIVITY_SECURE: `${API_BASE_URL}/api/uploadapp/upload-activity2`,
  
  /** @property {string} STORY_METRICS - Endpoint for story metrics and analytics */
  STORY_METRICS: `${API_BASE_URL}/api/uploadapp/story-metrics`,
  
  /** @property {string} UPLOAD_MEDIA_DETAILS - Endpoint for media upload details */
  UPLOAD_MEDIA_DETAILS: `${API_BASE_URL}/api/uploadapp/upload-media-details`,
  
  /** @property {string} CONTENT_DUMPS - Endpoint for content dump management */
  CONTENT_DUMPS: `${API_BASE_URL}/api/uploadapp/content-dumps`,
  
  /** @property {string} CONTENT_DUMP_DETAILS - Endpoint for content dump details */
  CONTENT_DUMP_DETAILS: `${API_BASE_URL}/api/uploadapp/content-dump-details`,
  
  /** @property {string} POST_ACTIVITY - Endpoint for post activity tracking */
  POST_ACTIVITY: `${API_BASE_URL}/api/uploadapp/post-activity`,
  
  /** @property {string} POST_ACTIVITY_DETAILS - Endpoint for detailed post activity */
  POST_ACTIVITY_DETAILS: `${API_BASE_URL}/api/uploadapp/post-activity/details`,
  
  /** 
   * @namespace LEADS
   * @description Endpoints for lead management and tracking
   */
  LEADS: {
    /** @property {string} GRID - Endpoint for lead grid view */
    GRID: `${API_BASE_URL}/api/leads/grid`,
    /** @property {string} RAW - Endpoint for raw lead data */
    RAW: `${API_BASE_URL}/api/leads/raw`,
    /** @property {string} RANKS - Endpoint for lead rankings */
    RANKS: `${API_BASE_URL}/api/leads/ranks`,
    /** 
     * @function CLIENT_WITH_PARTNER
     * @param {string} clientId - ID of the client
     * @returns {string} Endpoint for getting client with partner data
     */
    CLIENT_WITH_PARTNER: (clientId) => `${API_BASE_URL}/api/leads/client-with-partner/${clientId}`,
    /** 
     * @function UPDATE
     * @param {string} leadId - ID of the lead to update
     * @returns {string} Lead update endpoint
     */
    UPDATE: (leadId) => `${API_BASE_URL}/api/leads/${leadId}`,
    /** 
     * @function DELETE
     * @param {string} clientId - Client ID of the lead to delete
     * @returns {string} Lead delete endpoint
     */
    DELETE: (clientId) => `${API_BASE_URL}/api/leads/${clientId}`,
    /** 
     * @function SYNC
     * @param {string} leadId - ID of the lead to sync
     * @returns {string} Lead sync endpoint
     */
    SYNC: (leadId) => `${API_BASE_URL}/api/leads/sync/${leadId}`,
    
    /** 
     * @namespace NOTES
     * @description Endpoints for lead note management
     */
    NOTES: {
      GET: (leadId) => `${API_BASE_URL}/api/leads/notes/${leadId}`,
      CREATE: (leadId, noteType) => `${API_BASE_URL}/api/leads/notes/${leadId}/${noteType}`,
      UPDATE: (leadId, noteId) => `${API_BASE_URL}/api/leads/notes/${leadId}/${noteId}`,
      DELETE: (leadId, noteType, timestamp) => `${API_BASE_URL}/api/leads/notes/${leadId}/${noteType}/${timestamp}`
    },
    
    PAYOUT_INFO: (clientId) => `${API_BASE_URL}/api/leads/payout-info/${clientId}`,
    SYNC_PAYOUT: `${API_BASE_URL}/api/leads/sync-payout-email`,
    UPDATE_APPROVAL_STATUS: `${API_BASE_URL}/api/leads/update-approval-status`,
    APPROVAL_STATUS: (clientId) => `${API_BASE_URL}/api/leads/approval-status/${clientId}`,
    GENERATE_BIO: `${API_BASE_URL}/api/leads/generate-bio`,
    GET_SPLIT_INFO: (clientId) => `${API_BASE_URL}/api/payments/splits-company/${clientId}`,
    SAVE_SPLIT_INFO: () => `${API_BASE_URL}/api/payments/splits-company`
  },
  
  /** 
   * @namespace CONTRACTS
   * @description Endpoints for contract management
   */
  CONTRACTS: {
    SAVE_DRAFT: `${API_BASE_URL}/api/contracts/save_draft`,
    SIGN: `${API_BASE_URL}/api/contracts/sign`,
    SIGN_REP: `${API_BASE_URL}/api/contracts/sign/representative`,
    DOWNLOAD: `${API_BASE_URL}/api/contracts/download`,
    SEND_TO_CLIENT: `${API_BASE_URL}/api/contracts/send_to_client`,
    VERSIONS: (clientId) => `${API_BASE_URL}/api/contracts/versions/${clientId}`,
    CONTRACT: (clientId) => `${API_BASE_URL}/api/contracts/contract/${clientId}`,
    REQUEST_CHANGES: (clientId) => `${API_BASE_URL}/api/contracts/contract/request-changes/${clientId}`,
    
    // Document Repository Endpoints
    DOCUMENTS: {
      SAVE: `${API_BASE_URL}/api/contracts/documents/save`,
      SEARCH: `${API_BASE_URL}/api/contracts/documents/search`,
      CATEGORIES: `${API_BASE_URL}/api/contracts/documents/categories`,
      GET: (templateId) => `${API_BASE_URL}/api/contracts/documents/${templateId}`,
      UPDATE: (templateId) => `${API_BASE_URL}/api/contracts/documents/${templateId}`,
      DELETE: (templateId) => `${API_BASE_URL}/api/contracts/documents/${templateId}`,
      ANALYZE_COMMENTS: `${API_BASE_URL}/api/contracts/documents/analyze-comments`,
      
      // Document Revision/Comment System
      REVISIONS: {
        CREATE: `${API_BASE_URL}/api/contracts/documents/revisions/create`,
        GET: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}`,
        BY_CLIENT: (clientId) => `${API_BASE_URL}/api/contracts/documents/revisions/by-client/${clientId}`,
        UPDATE: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}`,
        SEND_EMAIL: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/send-email`,
        PUBLIC_VIEW: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/public/${revisionId}`,
        COMMENTS: {
          ADD: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments`,
          GET: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments`,
          CREATE: (revisionId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments`,
          UPDATE: (revisionId, commentId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments/${commentId}`,
          DELETE: (revisionId, commentId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments/${commentId}`,
          RESOLVE: (revisionId, commentId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments/${commentId}/resolve`,
          REJECT: (revisionId, commentId) => `${API_BASE_URL}/api/contracts/documents/revisions/${revisionId}/comments/${commentId}/reject`
        }
      }
    }
  },
  
  /** @property {string} ONBOARDING - Endpoint for user onboarding */
  ONBOARDING: `${API_BASE_URL}/api/onboarding`,
  
  /** 
   * @function ONBOARDING_STATUS
   * @description Endpoint for fetching lead onboarding status
   * @param {string} clientId - The client ID
   * @returns {string} The full endpoint URL
   */
  ONBOARDING_STATUS: (clientId) => `${API_BASE_URL}/api/lead/onboarding-status/${clientId}`,
  
  /** @property {string} TASKS - Endpoint for task management */
  TASKS: `${API_BASE_URL}/api/tasks`,
  
  /** @property {string} TASK_TEMPLATES - Endpoint for task templates */
  TASK_TEMPLATES: `${API_BASE_URL}/api/task-templates`,
  
  /** @property {string} CREATE_TASKS_FROM_TEMPLATES - Endpoint for creating tasks from templates */
  CREATE_TASKS_FROM_TEMPLATES: `${API_BASE_URL}/api/tasks/create-from-templates`,
  
  /** 
   * @namespace VISTA_GROUP
   * @description Endpoints for Vista group management
   */
  VISTA_GROUP: {
    CREATE: `${API_BASE_URL}/api/vista-group/create`,
  },
  
  /** 
   * @namespace TIKTOK
   * @description Endpoints for TikTok integration
   */
  TIKTOK: {
    BASE: `${API_BASE_URL}/api/tiktok`,
    DOWNLOAD: `${API_BASE_URL}/api/tiktok/download`,
    CLIENTS_LIST: `${API_BASE_URL}/api/tiktok/clients-list`,
    SESSIONS: (clientId) => `${API_BASE_URL}/api/tiktok/sessions/${clientId}`,
    SESSION_DETAILS: (clientId, sessionId) => `${API_BASE_URL}/api/tiktok/session/${clientId}/${sessionId}`,
    STATS: `${API_BASE_URL}/api/tiktok/stats`
  },
  
  /** 
   * @namespace PARTNERS
   * @description Endpoints for partner management
   */
  PARTNERS: {
    LIST: `${API_BASE_URL}/api/partners`,
    ADD: `${API_BASE_URL}/api/partners`,
    DELETE: (partnerId) => `${API_BASE_URL}/api/partners/${partnerId}`,
    BY_SLUG: (slug) => `${API_BASE_URL}/api/partners/by-slug/${slug}`,
    BACKFILL_SLUGS: `${API_BASE_URL}/api/partners/backfill-slugs`,
    MONETIZED: `${API_BASE_URL}/api/partners/monetized`,
    REFERRED: `${API_BASE_URL}/api/partners/referred`,
    GET_MONETIZED: (clientId) => `${API_BASE_URL}/api/partners/monetized/${clientId}`,
    GET_REFERRED: (clientId) => `${API_BASE_URL}/api/partners/referred/${clientId}`,
  },
  
  /** 
   * @namespace CDN
   * @description Endpoints for CDN management and operations
   */
  CDN: {
    BASE_URL: API_BASE_URL,
    CLIENT_INFO: (clientId) => `${API_BASE_URL}/api/cdn/client-info/${clientId}`,
    EDITOR_NOTES: `${API_BASE_URL}/api/cdn/editor-notes`,
    ADD_EDITOR_NOTE: `${API_BASE_URL}/api/cdn/editor-notes/add`,
    FOLDER_OPERATIONS: `${API_BASE_URL}/api/cdn/folder-operations`,
    DUMP_CONTENTS: `${API_BASE_URL}/api/cdn/dump-contents`,
    LIST_CONTENTS: `${API_BASE_URL}/api/cdn/list-contents`,
    SEARCH: `${API_BASE_URL}/api/cdn/search`,
    MEDIA_URL: BUNNY_CDN_URL,
    REORDER_FILES: `${API_BASE_URL}/api/cdn/reorder-files`,
    GENERATE_THUMBNAIL: `${API_BASE_URL}/api/cdn/generate-thumbnail`,
    TEST_THUMB_INSERT: `${API_BASE_URL}/api/cdn/test-thumb-insert`,
    UPDATE_CAPTION: `${API_BASE_URL}/api/cdn/update-caption`,
    SCAN_PATH: `${API_BASE_URL}/api/bunnyscan/scan-path`,
    REMOVE_FILE_RECORD: `${API_BASE_URL}/api/cdn/remove-file-record`,
    LIST_FOLDERS: `${API_BASE_URL}/api/cdn-mongo/list-folders`,
    FILE_GALLERY: `${API_BASE_URL}/api/cdn-mongo/file-gallery`,
    COLLECTIONS: `${API_BASE_URL}/api/cdn-mongo/collections`,
    COLLECTION: (name) => `${API_BASE_URL}/api/cdn-mongo/collections/${name}`,
  },
  
  /** 
   * @namespace CONTENT_DUMP
   * @description Endpoints for content dump operations
   */
  CONTENT_DUMP: {
    GET: (userId) => `${API_BASE_URL}/api/content-dump/${userId}`,
    CREATE: (userId) => `${API_BASE_URL}/api/content-dump/${userId}`,
    UPDATE: (userId) => `${API_BASE_URL}/api/content-dump/${userId}`,
  },
  
  /** 
   * @namespace UPLOAD
   * @description Endpoints for upload management
   */
  UPLOAD: {
    INIT_SESSION: `${API_BASE_URL}/api/uploadapp/init-session`,
    MEDIA_DETAILS: `${API_BASE_URL}/api/uploadapp/upload-media-details`,
    CONTENT_NOTES: `${API_BASE_URL}/api/uploadapp/content-notes`,
    APPROVE: `${API_BASE_URL}/api/uploadapp/content-notes/approve`,
    UPDATE_EDITOR_NOTE: `${API_BASE_URL}/api/uploadapp/update-editor-note`,
    GET_EDITOR_NOTES: `${API_BASE_URL}/api/uploadapp/editor-notes`,
    UPLOAD_VIDEOS: `${API_BASE_URL}/api/uploadapp/upload-videos`,
    SCAN_MEDIA: `${API_BASE_URL}/api/content-scan/scan-media`,
    DELETE_CONTENT: (clientId, sessionFolder) => 
      `${API_BASE_URL}/api/uploadapp/delete-content/${clientId}/${sessionFolder}`,
    UPDATE_FLAG_STATUS: (clientId, folderId, fileName) => 
      `${API_BASE_URL}/api/uploadapp/content-flags/${clientId}/${folderId}/${fileName}/status`,
    VIDEO_SUMMARY: (clientId, sessionId, fileName) => 
      `${API_BASE_URL}/api/uploadapp/video-summary/${clientId}/${sessionId}/${fileName}`,
  },
  
  /** @property {string} SPOTLIGHTS - Endpoint for spotlight content */
  SPOTLIGHTS: `${API_BASE_URL}/api/uploadapp/spotlights`,

  /** @property {string} SPOTLIGHT_DETAILS - Endpoint for spotlight content details */
  SPOTLIGHT_DETAILS: `${API_BASE_URL}/api/uploadapp/spotlight-details`,
  
  /** 
   * @namespace EMPLOYEES
   * @description Endpoints for employee management
   */
  EMPLOYEES: {
    SIGNUP: `${API_BASE_URL}/api/employees/signup`,
    PARTNER_SEARCH: (query) => `${API_BASE_URL}/api/partners/search/${query}`,
    SEARCH: `${API_BASE_URL}/api/leads/employees/search`,
    GET_ASSIGNED: `${API_BASE_URL}/api/leads/employees/assigned`,
    ASSIGN: `${API_BASE_URL}/api/leads/employees/assign`,
    UNASSIGN: `${API_BASE_URL}/api/leads/employees/unassign`,
    LIST: `${API_BASE_URL}/api/employees/`,
    GET: (userId) => `${API_BASE_URL}/api/employees/${userId}`,
    METRICS: (userId) => `${API_BASE_URL}/api/employees/${userId}/metrics`,
    TIMESHEET: (userId) => `${API_BASE_URL}/api/employees/${userId}/timesheet`,
    INVOICES: (userId) => `${API_BASE_URL}/api/employees/${userId}/invoices`,
    DOWNLOAD_INVOICE: (userId, invoiceId) => `${API_BASE_URL}/api/employees/${userId}/invoices/${invoiceId}/download`,
    UPDATE_REVENUE_VISIBILITY: (userId) => `${API_BASE_URL}/api/employees/${userId}/revenue-visibility`
  },
  
  /** 
   * @namespace ANALYTICS
   * @description Endpoints for analytics and reporting
   */
  ANALYTICS: {
    GET_CLIENTS: `${API_BASE_URL}/api/analytics/clients`,
    GET_SNAPCHAT: `${API_BASE_URL}/api/analytics/snapchat`,
    GET_SNAPCHAT_SINGLE: `${API_BASE_URL}/api/analytics/snapchat/single`,
    UPLOAD_SNAPCHAT: `${API_BASE_URL}/api/analytics/upload/snapchat`,
    GET_SNAPCHAT_METRICS: `${API_BASE_URL}/api/analytics/snapchat/metrics`,
    GET_MOBILE: `${API_BASE_URL}/api/analytics/mobile`,
    SYNC_VISTA: `${API_BASE_URL}/api/analytics/sync/vista`
  },
  
  /** 
   * @namespace TIMESHEET
   * @description Endpoints for timesheet management
   */
  TIMESHEET: {
    ENTRIES: `${API_BASE_URL}/api/timesheet/entries`,
    SEARCH_ASSIGNEES: (query) => `${API_BASE_URL}/api/timesheet/search_assignees?query=${query}`,
    PREVIEW_INVOICE: `${API_BASE_URL}/api/timesheet/preview-invoice`,
    SUBMIT_INVOICE: `${API_BASE_URL}/api/timesheet/submit-invoice`,
    SET_QB_ID: `${API_BASE_URL}/api/timesheet/set-qb-id`,
    UPDATE_ENTRY: (entryId) => `${API_BASE_URL}/api/timesheet/entries/${entryId}`,
    DELETE_ENTRY: (entryId) => `${API_BASE_URL}/api/timesheet/entries/${entryId}`,
  },
  
  /** 
   * @namespace DEMO
   * @description Endpoints for demo functionality
   */
  DEMO: {
    VERIFY_PASSWORD: `${API_BASE_URL}/api/demo/verify-password`,
  },
  
  /** 
   * @namespace SUPPORT
   * @description Endpoints for support functionality
   */
  SUPPORT: {
    SUBMIT: `${API_BASE_URL}/api/support/submit`,
  },
  
  /** 
   * @namespace QUEUE
   * @description Endpoints for queue management
   */
  QUEUE: {
    SPOTLIGHT: `${API_BASE_URL}/api/spot-queue/build`,
    SAVED: `${API_BASE_URL}/api/saved-queue/build`,
    PROCESS_SAVED: `${API_BASE_URL}/api/saved-queue/process-saved-make`
  },
  
  /** @property {string} SAVED_ACTIVITY - Endpoint for saved activity tracking */
  SAVED_ACTIVITY: `${API_BASE_URL}/api/uploadapp/saved-activity`,
  
  /** @property {string} SAVED_MEDIA_DETAILS - Endpoint for saved media details */
  SAVED_MEDIA_DETAILS: `${API_BASE_URL}/api/uploadapp/saved-media-details`,
  
  /** 
   * @namespace TWELVE_LABS
   * @description Endpoints for Twelve Labs integration
   */
  TWELVE_LABS: {
    SCAN_UPLOADS: `${API_BASE_URL}/api/twelve-labs/scan-uploads`,
    SCAN_INDEXES: `${API_BASE_URL}/api/twelve-labs/scan-for-indexes`,
    UPLOAD_VIDEOS: `${API_BASE_URL}/api/twelve-labs/upload-videos`,
    SEARCH_INDEX: `${API_BASE_URL}/api/twelve-labs/search-index`,
    SEARCH_ALL: `${API_BASE_URL}/api/twelve-labs/search-all-indexes`,
    MATCH_RESULTS: `${API_BASE_URL}/api/twelve-labs/match-search-results`,
    SUMMARIZE_VIDEOS: `${API_BASE_URL}/api/twelve-labs/summarize-videos`,
    SUMMARIZE_VIDEO: (videoId) => `${API_BASE_URL}/api/twelve-labs/summarize-video/${videoId}`,
    SYNC_SUMMARIES: `${API_BASE_URL}/api/twelve-labs/sync-summaries`,
  },
  
  /** 
   * @namespace CONTENT_SCAN
   * @description Endpoints for content scanning functionality
   */
  CONTENT_SCAN: {
    SCAN_SESSION: `${API_BASE_URL}/api/content-scan/scan-session`,
    SCAN_MEDIA: `${API_BASE_URL}/api/content-scan/scan-media`,
  },
  
  /** @property {string} CONTENT_FLAGS - Endpoint for content flag management */
  CONTENT_FLAGS: `${API_BASE_URL}/api/uploadapp/content-flags`,
  
  /** 
   * @namespace VIDEO_SUMMARY
   * @description Endpoints for video summary functionality
   */
  VIDEO_SUMMARY: {
    SYNC_CONTENT: `${API_BASE_URL}/api/ai-review/video-summary/sync-content`,
    SAVE_REVIEW: (videoId) => `${API_BASE_URL}/api/ai-review/video-summary/save-review/${videoId}`,
    GET_PROMPTS: `${API_BASE_URL}/api/ai-review/video-summary/prompts`,
    GENERATE_PROMPT: `${API_BASE_URL}/api/ai-review/video-summary/generate-prompt`,
    ACTIVATE_PROMPT: `${API_BASE_URL}/api/ai-review/video-summary/activate-prompt`,
  },
  
  /** 
   * @namespace SURVEY
   * @description Endpoints for survey functionality
   */
  SURVEY: {
    QUESTIONS: `${API_BASE_URL}/api/client_survey/questions`,
    SUBMIT: `${API_BASE_URL}/api/client_survey/submit`,
    SUBMIT_KEY_INFO: `${API_BASE_URL}/api/client_survey/key_info`,
    VALIDATE_IOS: `${API_BASE_URL}/api/client_survey/validate_ios`,
    RESPONSES: {
      KEY: `${API_BASE_URL}/api/client_survey/responses/key`,
      DETAILED: `${API_BASE_URL}/api/client_survey/responses/detailed`,
      USERS: `${API_BASE_URL}/api/client_survey/users`
    },
    ADMIN: {
      ADD_QUESTION: `${API_BASE_URL}/api/client_survey/questions/add`,
      UPDATE_QUESTIONS: `${API_BASE_URL}/api/client_survey/questions/update`,
      GET_SECTIONS: `${API_BASE_URL}/api/client_survey/sections`
    }
  },
  
  /** 
   * @namespace CHAT
   * @description Endpoints for AI chat functionality
   */
  CHAT: {
    SEND: `${API_BASE_URL}/api/chat`,
    HISTORY: (clientId) => `${API_BASE_URL}/api/chat/history/${clientId}`,
    EMPLOYEES: `${API_BASE_URL}/api/employees`,
    NOTES: {
      SEARCH: (clientId) => `${API_BASE_URL}/api/notes/search/${clientId}`,
      SEARCH_CONTENT: (clientId) => `${API_BASE_URL}/api/notes/search-content/${clientId}`,
      SEARCH_DATE: (clientId) => `${API_BASE_URL}/api/notes/search-date/${clientId}`
    }
  },
  
  /** 
   * @namespace DESKTOP_UPLOAD
   * @description Endpoints for desktop upload functionality
   */
  DESKTOP_UPLOAD: {
    UPLOAD: `${API_BASE_URL}/api/desktop-upload/upload`,
    PROGRESS: (fileName) => `${API_BASE_URL}/api/desktop-upload/progress/${fileName}`,
    USERS: `${API_BASE_URL}/api/desktop-upload/users`,
    FOLDERS: (userId) => `${API_BASE_URL}/api/desktop-upload/folders/${userId}`
  },
  
  /** 
   * @namespace PAYMENTS
   * @description Endpoints for payment processing
   */
  PAYMENTS: {
    CREATE_PAYEE: `${API_BASE_URL}/api/payments/payee`,
    GET_PAYEE: (email) => `${API_BASE_URL}/api/payments/payee/${email}`,
    SEARCH_PAYOUTS: `${API_BASE_URL}/api/payments/search-payouts`,
    SEARCH_PAYEES: `${API_BASE_URL}/api/payments/search-payees`,
    GET_SPLIT_PROFILE: (clientId) => `${API_BASE_URL}/api/payments/split-profile/${clientId}`,
    SAVE_SPLIT_PROFILE: `${API_BASE_URL}/api/payments/split-profile`,
    DELETE_SPLIT_PROFILE: (clientId, payeeId) => `${API_BASE_URL}/api/payments/split-profile/${clientId}/${payeeId}`,
    PAYEE_FORM: `${API_BASE_URL}/payee-form`,
    SPLITS: {
      LIST: `${API_BASE_URL}/api/payments/splits`,
      CREATE: `${API_BASE_URL}/api/payments/splits`,
      UPDATE: (splitId) => `${API_BASE_URL}/api/payments/splits/${splitId}`,
      DELETE: (splitId) => `${API_BASE_URL}/api/payments/splits/${splitId}`,
      GET: (splitId) => `${API_BASE_URL}/api/payments/splits/${splitId}`,
    },
    QUICKBOOKS: {
      SYNC: `${API_BASE_URL}/api/payments/quickbooks/sync`,
      STATUS: (email) => `${API_BASE_URL}/api/payments/quickbooks/sync-status/${email}`,
      BULK_SYNC: `${API_BASE_URL}/api/payments/quickbooks/bulk-sync`
    },
    MERCURY: {
      CREATE_RECIPIENT: `${API_BASE_URL}/api/payments/mercury/recipient`,
      GET_RECIPIENT: (email) => `${API_BASE_URL}/api/payments/mercury/recipient/${email}`,
      UPDATE_RECIPIENT: (email) => `${API_BASE_URL}/api/payments/mercury/recipient/${email}`,
      LIST_RECIPIENTS: `${API_BASE_URL}/api/payments/mercury/recipients`,
      SEND_PAYMENT: `${API_BASE_URL}/api/payments/mercury/payment`,
      BATCH_PAYMENTS: `${API_BASE_URL}/api/payments/mercury/payments/batch`,
      PAYMENT_STATUS: (paymentId) => `${API_BASE_URL}/api/payments/mercury/payment/${paymentId}/status`,
      TRANSACTIONS: `${API_BASE_URL}/api/payments/mercury/transactions`,
      ACCOUNT_INFO: `${API_BASE_URL}/api/payments/mercury/account`,
      MIGRATE_PAYEE: (email) => `${API_BASE_URL}/api/payments/mercury/migrate-payee/${email}`
    },
    DASHBOARD: {
      MONTHS: `${API_BASE_URL}/api/payments/dashboard/months`,
      PENDING: `${API_BASE_URL}/api/payments/dashboard/pending`,
      PROCESSED: `${API_BASE_URL}/api/payments/dashboard/processed`,
      APPROVE: `${API_BASE_URL}/api/payments/dashboard/approve`,
      SET_PROVIDER: `${API_BASE_URL}/api/payments/dashboard/set-payee-provider`,
      PAYEE_PROVIDERS: `${API_BASE_URL}/api/payments/dashboard/payee-providers`
    }
  },
  
  /** 
   * @namespace CDN_MONGO
   * @description Endpoints for CDN MongoDB operations
   */
  CDN_MONGO: {
    GET_USERS: `${API_BASE_URL}/api/cdn-mongo/get-users`,
    LIST_FOLDERS: `${API_BASE_URL}/api/cdn-mongo/list-folders`,
    FILE_GALLERY: `${API_BASE_URL}/api/cdn-mongo/file-gallery`,
    GET_CLIENT_INFO: (clientId) => `${API_BASE_URL}/api/cdn-mongo/get-client-info/${clientId}`,
    PARENT_PATH: `${API_BASE_URL}/api/cdn-mongo/parent-path`,
    EDITOR_NOTES: (clientId) => `/api/cdn-mongo/editor-notes/${clientId}`
  },
  
  /** 
   * @namespace CLIENTS
   * @description Endpoints for client management
   */
  CLIENTS: {
    SIGNED: `${API_BASE_URL}/api/clients/signed`,
    CHECK_ID: (clientId) => `${API_BASE_URL}/api/clients/check-id/${clientId}`,
    INTAKE: `${API_BASE_URL}/api/clients/intake`,
  },
  
  /** 
   * @namespace MESSAGES
   * @description Endpoints for message management
   */
  MESSAGES: {
    GET_AI_NOTES: (clientId, date) => `${API_BASE_URL}/api/messages/ai-notes/${clientId}/${date}`,
    GET_AI_TASKS: (clientId, date) => `${API_BASE_URL}/api/messages/tasks/${clientId}/${date}`,
    GET_UNREVIEWED_NOTES: (clientId) => `${API_BASE_URL}/api/messages/unreviewed-notes/${clientId}`,
    GET_UNREVIEWED_TASKS: (clientId) => `${API_BASE_URL}/api/messages/unreviewed-tasks/${clientId}`,
    UPDATE_NOTE_REVIEW: (clientId) => `${API_BASE_URL}/api/messages/review-note-status/${clientId}`,
    UPDATE_TASK_REVIEW: (clientId) => `${API_BASE_URL}/api/messages/review-task-status/${clientId}`
  },
/** 
   * @namespace RFP
   * @description Endpoints for RFP (Request for Proposals) management
   */
  EMAIL: {
    OAUTH_CALLBACK: `${API_BASE_URL}/emailalias`
  },

  RFP: {
  /** @property {string} ANALYZE - Endpoint for analyzing RFP and matching with clients */
  ANALYZE: `${API_BASE_URL}/api/rfp/analyze`,
  
  /** @property {string} REVERSE_PROPOSAL - Endpoint for creating reverse proposals for specific clients */
  REVERSE_PROPOSAL: `${API_BASE_URL}/api/rfp/reverse-proposal`,
  
  /** @property {string} GENERATE_BRIEF - Endpoint for generating campaign briefs */
  GENERATE_BRIEF: `${API_BASE_URL}/api/rfp/generate-brief`,
  
  /** @property {string} GENERATE_PROPOSAL_EMAIL - Endpoint for generating proposal emails */
  GENERATE_PROPOSAL_EMAIL: `${API_BASE_URL}/api/rfp/generate-proposal-email`,
  
  /** @property {string} GENERATE_BRIEF_EMAIL - Endpoint for generating brief emails */
  GENERATE_BRIEF_EMAIL: `${API_BASE_URL}/api/rfp/generate-brief-email`,
  
  /** @property {string} HISTORY - Endpoint for RFP history */
  HISTORY: `${API_BASE_URL}/api/rfp/history`,
  
  /** @property {string} BRANDS - Endpoint for brand management */
  BRANDS: `${API_BASE_URL}/api/rfp/brands`,
  
  /** 
   * @function SAVE_RFP
   * @param {string} rfpId - ID of the RFP to save
   * @returns {string} RFP save endpoint
   */
  SAVE_RFP: (rfpId) => `${API_BASE_URL}/api/rfp/save/${rfpId}`,
  
  /** 
   * @function GET_RFP
   * @param {string} rfpId - ID of the RFP to retrieve
   * @returns {string} RFP retrieval endpoint
   */
  GET_RFP: (rfpId) => `${API_BASE_URL}/api/rfp/${rfpId}`,
  
  /** 
   * @function DELETE_RFP
   * @param {string} rfpId - ID of the RFP to delete
   * @returns {string} RFP deletion endpoint
   */
  DELETE_RFP: (rfpId) => `${API_BASE_URL}/api/rfp/${rfpId}`,
  
  /** 
   * @function UPDATE_RFP_STATUS
   * @param {string} rfpId - ID of the RFP to update
   * @returns {string} RFP status update endpoint
   */
  UPDATE_RFP_STATUS: (rfpId) => `${API_BASE_URL}/api/rfp/${rfpId}/status`
},

  /** 
   * @namespace SCREENSHOTS
   * @description Endpoints for Instagram screenshot processing
   */
  SCREENSHOTS: {
    /** @property {string} UPLOAD - Endpoint for uploading and processing Instagram screenshots */
    UPLOAD: `${API_BASE_URL}/api/screenshots/upload`,
    
    /** @property {string} LEADS - Endpoint for retrieving processed leads */
    LEADS: `${API_BASE_URL}/api/screenshots/leads`,
    
    /** @property {string} HEALTH - Endpoint for health check */
    HEALTH: `${API_BASE_URL}/api/screenshots/health`
  },

  /** 
   * @namespace WHATSAPP
   * @description Endpoints for WhatsApp webhook integration
   */
  WHATSAPP: {
    /** @property {string} WEBHOOK - Endpoint for WhatsApp webhooks (proxies to unified-chat) */
    WEBHOOK: `${API_BASE_URL}/wa/webhook`
  },

  /** 
   * @namespace SNAP_MONETIZATION
   * @description Endpoints for Snap Monetization form functionality
   */
  SNAP_MONETIZATION: {
    /** @property {string} SUBMIT - Endpoint for submitting Snap Monetization form */
    SUBMIT: `${API_BASE_URL}/api/snap-monetization/submit`,
    
    /** @property {string} RESPONSES - Endpoint for retrieving all form responses (admin) */
    RESPONSES: `${API_BASE_URL}/api/snap-monetization/responses`,
    
    /** @property {string} MY_RESPONSE - Endpoint for retrieving current user's response */
    MY_RESPONSE: `${API_BASE_URL}/api/snap-monetization/my-response`,
    
    /** @property {string} UPDATE - Endpoint for updating existing form */
    UPDATE: `${API_BASE_URL}/api/snap-monetization/update`,
    
    /** @property {string} DELETE - Endpoint for deleting form */
    DELETE: `${API_BASE_URL}/api/snap-monetization/delete`,
    
    /** @property {function} CREATOR_SHEET - Endpoint for downloading creator one-sheet PDF */
    CREATOR_SHEET: (clientId) => `${API_BASE_URL}/api/snap-monetization/creator-sheet/${clientId}`,
    
    /** @property {function} VIDEO_DOWNLOAD - Endpoint for getting video download URL */
    VIDEO_DOWNLOAD: (clientId) => `${API_BASE_URL}/api/snap-monetization/video-download/${clientId}`
  },

  /** 
   * @namespace PIXEL_MONETIZATION
   * @description Endpoints for Pixel Talent Monetization form functionality
   */
  PIXEL_MONETIZATION: {
    /** @property {string} SUBMIT - Endpoint for submitting Pixel Monetization form (uses snap-monetization backend) */
    SUBMIT: `${API_BASE_URL}/api/snap-monetization/submit`,
    
    /** @property {string} MY_RESPONSE - Endpoint for retrieving current user's response */
    MY_RESPONSE: `${API_BASE_URL}/api/snap-monetization/my-response`,
    
    /** @property {string} UPDATE - Endpoint for updating existing form */
    UPDATE: `${API_BASE_URL}/api/snap-monetization/update`,
    
    /** @property {string} RESPONSES - Endpoint for retrieving all form responses (admin) */
    RESPONSES: `${API_BASE_URL}/api/snap-monetization/responses`
  },

  /** 
   * @namespace TOS_TEMPLATES
   * @description Endpoints for Terms of Service template management
   */
  TOS_TEMPLATES: {
    /** @property {string} LIST - Endpoint for listing all TOS templates */
    LIST: `${API_BASE_URL}/api/tos-templates`,
    
    /** @property {string} SAVE - Endpoint for saving a TOS template */
    SAVE: `${API_BASE_URL}/api/tos-templates`,
    
    /** @property {function} GET - Endpoint for getting a specific TOS template by key */
    GET: (key) => `${API_BASE_URL}/api/tos-templates/${key}`,
    
    /** @property {function} DELETE - Endpoint for deleting a TOS template */
    DELETE: `${API_BASE_URL}/api/tos-templates`
  },

  /** 
   * @namespace MESSAGING
   * @description Endpoints for client messaging integration with iMessage bridge
   */
  MESSAGING: {
    /** @property {string} NORMALIZE_PHONE - Endpoint for normalizing phone numbers to E.164 format */
    NORMALIZE_PHONE: `${API_BASE_URL}/api/messaging/normalize-phone`,
    
    /** @property {string} SYNC_GROUP_CHAT - Endpoint for syncing group chat ID for a client */
    SYNC_GROUP_CHAT: `${API_BASE_URL}/api/messaging/sync-group-chat`,
    
    /** @property {function} GET_CLIENT - Endpoint for getting messaging info for a client */
    GET_CLIENT: (clientId) => `${API_BASE_URL}/api/messaging/client/${clientId}`,
    
    /** @property {function} TOGGLE_AUTO_MESSAGING - Endpoint for toggling auto-messaging for a client */
    TOGGLE_AUTO_MESSAGING: (clientId) => `${API_BASE_URL}/api/messaging/client/${clientId}/auto-messaging`,
    
    /** @property {string} GET_CONTACTS - Endpoint for getting all contacts */
    GET_CONTACTS: `${API_BASE_URL}/api/messaging/contacts`,
    
    /** @property {string} ADD_CONTACT - Endpoint for adding a new contact */
    ADD_CONTACT: `${API_BASE_URL}/api/messaging/contacts`,
    
    /** @property {function} UPDATE_CONTACT - Endpoint for updating a contact */
    UPDATE_CONTACT: (clientId) => `${API_BASE_URL}/api/messaging/contacts/${clientId}`,
    
    /** @property {function} DELETE_CONTACT - Endpoint for deleting a contact */
    DELETE_CONTACT: (clientId) => `${API_BASE_URL}/api/messaging/contacts/${clientId}`
  },

  /** 
   * @namespace CALENDAR
   * @description Endpoints for Google Calendar and Meet integration
   */
  CALENDAR: {
    /** @property {function} ASSIGNED_EMPLOYEES - Get employees assigned to a client */
    ASSIGNED_EMPLOYEES: (clientId) => `${API_BASE_URL}/api/calendar/clients/${clientId}/assigned-employees`,
    
    /** @property {function} CREATE_MEET - Create a Google Meet call for a client */
    CREATE_MEET: (clientId) => `${API_BASE_URL}/api/calendar/clients/${clientId}/create-meet`,
    
    /** @property {function} GET_MEETINGS - Get upcoming meetings for a client */
    GET_MEETINGS: (clientId) => `${API_BASE_URL}/api/calendar/clients/${clientId}/meetings`,
    
    /** @property {function} CANCEL_MEETING - Cancel a meeting by event ID */
    CANCEL_MEETING: (eventId) => `${API_BASE_URL}/api/calendar/meetings/${eventId}`,
    
    /** @property {string} TEST - Test calendar service connectivity */
    TEST: `${API_BASE_URL}/api/calendar/test-calendar-service`
  },

  /** 
   * @namespace NOTIFICATIONS
   * @description Endpoints for client activity notifications
   */
  NOTIFICATIONS: {
    /** @property {string} GET_NOTIFICATIONS - Endpoint for retrieving notifications with filters */
    GET_NOTIFICATIONS: `${API_BASE_URL}/api/notifications`,
    
    /** @property {string} UNREAD_COUNT - Endpoint for getting unread notification count */
    UNREAD_COUNT: `${API_BASE_URL}/api/notifications/unread-count`,
    
    /** @property {function} MARK_READ - Endpoint for marking notification as read */
    MARK_READ: (notificationId) => `${API_BASE_URL}/api/notifications/${notificationId}/read`,
    
    /** @property {string} MARK_ALL_READ - Endpoint for marking all notifications as read */
    MARK_ALL_READ: `${API_BASE_URL}/api/notifications/mark-all-read`,
    
    /** @property {string} GET_PREFERENCES - Endpoint for getting notification preferences */
    GET_PREFERENCES: `${API_BASE_URL}/api/notifications/preferences`,
    
    /** @property {string} UPDATE_PREFERENCES - Endpoint for updating notification preferences */
    UPDATE_PREFERENCES: `${API_BASE_URL}/api/notifications/preferences`,
    
    /** @property {function} DELETE - Endpoint for deleting a notification */
    DELETE: (notificationId) => `${API_BASE_URL}/api/notifications/${notificationId}`,
    
    /** @property {string} STATS - Endpoint for getting notification statistics */
    STATS: `${API_BASE_URL}/api/notifications/stats`
  },

  /** 
   * @namespace STAFF_NOTIFICATIONS
   * @description Endpoints for staff notification management (email, SMS, internal)
   */
  STAFF_NOTIFICATIONS: {
    /** @property {string} GET_EMPLOYEES - Endpoint for getting all employees */
    GET_EMPLOYEES: `${API_BASE_URL}/api/staff-notifications/employees`,
    
    /** @property {function} GET_PREFERENCES - Endpoint for getting staff notification preferences */
    GET_PREFERENCES: (userId) => `${API_BASE_URL}/api/staff-notifications/preferences/${userId}`,
    
    /** @property {function} UPDATE_PREFERENCES - Endpoint for updating staff notification preferences */
    UPDATE_PREFERENCES: (userId) => `${API_BASE_URL}/api/staff-notifications/preferences/${userId}`,
    
    /** @property {function} UPDATE_CHANNEL - Endpoint for updating a specific notification channel */
    UPDATE_CHANNEL: (userId) => `${API_BASE_URL}/api/staff-notifications/channel/${userId}`,
    
    /** @property {function} TEST_NOTIFICATION - Endpoint for sending test notification */
    TEST_NOTIFICATION: (userId, channel) => `${API_BASE_URL}/api/staff-notifications/test-notification/${userId}?channel=${channel}`,
    
    /** @property {string} SUMMARY - Endpoint for getting notification settings summary */
    SUMMARY: `${API_BASE_URL}/api/staff-notifications/summary`,
    
    /** @property {function} RESET_PREFERENCES - Endpoint for resetting preferences to defaults */
    RESET_PREFERENCES: (userId) => `${API_BASE_URL}/api/staff-notifications/preferences/${userId}`
  },

  /**
   * @namespace ADMIN
   * @description Admin-only endpoints for system management
   */
  ADMIN: {
    /** @property {string} ROLE_PERMISSIONS - Base endpoint for role permissions management */
    ROLE_PERMISSIONS: `${API_BASE_URL}/api/admin/role-permissions`,
    
    /** @property {string} AVAILABLE_FEATURES - Endpoint for getting all available features */
    AVAILABLE_FEATURES: `${API_BASE_URL}/api/admin/role-permissions/features`,
    
    /** @property {string} AVAILABLE_ROLES - Endpoint for getting all available roles */
    AVAILABLE_ROLES: `${API_BASE_URL}/api/admin/role-permissions/roles`,
    
    /** @property {function} GET_ROLE_PERMISSIONS - Endpoint for getting specific role permissions */
    GET_ROLE_PERMISSIONS: (role) => `${API_BASE_URL}/api/admin/role-permissions/${role}`,
    
    /** @property {function} UPDATE_ROLE_PERMISSIONS - Endpoint for updating role permissions */
    UPDATE_ROLE_PERMISSIONS: (role) => `${API_BASE_URL}/api/admin/role-permissions/${role}`,
    
    /** @property {string} AUDIT_LOG - Endpoint for getting permission changes audit log */
    AUDIT_LOG: `${API_BASE_URL}/api/admin/role-permissions/audit-log`
  }
};